import { FormField, TextInput, Segmented, type SegmentedOption } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ReceiverEchoLine } from "./ReceiverEchoLine";
import { PHONE_RE, type InstaReceiver, type InstaState } from "./payment-form";

type InstaPayFieldsProps = {
  value: InstaState;
  onChange: (next: InstaState) => void;
};

// Fields flow into a responsive 2-column grid: every input is the width of its
// cell (so widths stay consistent), and paired fields sit on one row when
// there's room.
const GRID = "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3";

export function InstaPayFields({ value, onChange }: InstaPayFieldsProps) {
  const t = useTranslations();
  const update = (partial: Partial<InstaState>) =>
    onChange({ ...value, ...partial });

  const options: SegmentedOption<InstaReceiver>[] = [
    { value: "PHONE", label: t(keys.onboarding.paymentInstaPayPhone) },
    { value: "IPA", label: t(keys.seller.pmInstaAddress) },
    { value: "BANK_ACCOUNT", label: t(keys.seller.pmInstaBank) },
    { value: "CARD", label: t(keys.seller.pmInstaCard) },
  ];

  const phoneInvalid =
    value.phone.trim().length > 0 && !PHONE_RE.test(value.phone.trim());

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-[color:var(--text-muted)]">
          {t(keys.seller.pmInstaReceiverLabel)}
        </span>
        <Segmented
          value={value.receiver}
          onChange={(receiver) => update({ receiver })}
          options={options}
          ariaLabel={t(keys.seller.pmInstaReceiverLabel)}
        />
      </div>

      {value.receiver === "PHONE" ? (
        <div className={GRID}>
          <div className="flex flex-col gap-1.5">
            <FormField label={t(keys.onboarding.paymentInstaPayPhone)} name="ip-phone">
              <TextInput
                name="ip-phone"
                inputMode="numeric"
                dir="ltr"
                hasError={phoneInvalid}
                placeholder="01xxxxxxxxx"
                value={value.phone}
                onChange={(e) => update({ phone: e.target.value })}
              />
            </FormField>
            <ReceiverEchoLine value={value.phone} />
          </div>
        </div>
      ) : null}

      {value.receiver === "IPA" ? (
        <div className={GRID}>
          <div className="flex flex-col gap-1.5">
            <FormField label={t(keys.seller.pmInstaAddress)} name="ip-ipa">
              <TextInput
                name="ip-ipa"
                dir="ltr"
                placeholder="name@instapay"
                value={value.ipa}
                onChange={(e) => update({ ipa: e.target.value })}
              />
            </FormField>
            <ReceiverEchoLine value={value.ipa} />
          </div>
        </div>
      ) : null}

      {value.receiver === "BANK_ACCOUNT" ? (
        <div className={GRID}>
          <FormField label={t(keys.seller.pmBankName)} name="ip-bank-name">
            <TextInput
              name="ip-bank-name"
              value={value.bankName}
              onChange={(e) => update({ bankName: e.target.value })}
            />
          </FormField>
          <FormField label={t(keys.seller.pmReceiverName)} name="ip-bank-holder">
            <TextInput
              name="ip-bank-holder"
              value={value.holder}
              onChange={(e) => update({ holder: e.target.value })}
            />
          </FormField>
          <div className="flex flex-col gap-1.5">
            <FormField label={t(keys.seller.pmBankAccountMode)} name="ip-bank-account">
              <TextInput
                name="ip-bank-account"
                inputMode="numeric"
                dir="ltr"
                value={value.account}
                onChange={(e) => update({ account: e.target.value })}
              />
            </FormField>
            <ReceiverEchoLine value={value.account} />
          </div>
        </div>
      ) : null}

      {value.receiver === "CARD" ? (
        <div className={GRID}>
          <FormField label={t(keys.seller.pmCardHolder)} name="ip-card-holder">
            <TextInput
              name="ip-card-holder"
              value={value.cardHolder}
              onChange={(e) => update({ cardHolder: e.target.value })}
            />
          </FormField>
          <div className="flex flex-col gap-1.5">
            <FormField label={t(keys.seller.pmCardNumber)} name="ip-card-number">
              <TextInput
                name="ip-card-number"
                inputMode="numeric"
                dir="ltr"
                value={value.cardNumber}
                onChange={(e) => update({ cardNumber: e.target.value })}
              />
            </FormField>
            <ReceiverEchoLine value={value.cardNumber} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
